/** The material kit: carved wood, cut stone, gilt edging, parchment.
 *
 * Every screen in the game is built from these six primitives plus `Icon`. They
 * carry no game knowledge at all - a `Panel` does not know what a hero is - so
 * `packages/ui/src/screens/**` can lean on them without importing the adventure
 * shell. That separation is the reason this module is worth its own file.
 */

import {
  useEffect, useMemo, useRef, useState,
  type ButtonHTMLAttributes, type CSSProperties, type ReactNode, type RefObject,
} from "react";
import { ALIAS_DEFS_ID, ALIAS_FILTER, THEME_CSS, THEME_STYLE_ID } from "./css.js";
import { StyleLayer, useStyleSheet } from "./styleSheet.js";
import { deviceCapFloor } from "@game/render/text/glyphs";
import { FONT_SIZES, deviceSnappedFontPx, minDeviceTypePx, pickScale } from "./tokens.js";
import { fitSpriteBox, spriteStyleBox, type Atlas } from "./atlas.js";
import { useAtlas } from "../hooks/useAtlas.js";
import { usePhoneLayout } from "./phone.js";

// ------------------------------------------------------------------ provider

let aliasRefs = 0;

/**
 * The scale step and type sizes the kit's media blocks WOULD have chosen for
 * a viewport of this many DEVICE pixels, as inline custom properties, with
 * every type size snapped to whole device pixels under ratio `dpr`.
 *
 * Needed only under a browser zoomed OUT. A media query measures the CSS
 * viewport, which at 80% zoom on a 1366x768 window is 1707x960 - a size the
 * steps in `tokens.ts` call 1.25 - and a step of 1.25 on a page drawn at 0.8
 * is 1.0 on the glass: the frame the player just zoomed out of, restated in
 * more CSS pixels. Picking the step from the device size instead keeps the
 * step the window had at 100% (1 here, 1.5 on a 1080p monitor) and lets the
 * zoom do what was asked: the same layout, four fifths the size. Everything
 * below `.hm-root` reads these variables, so restating them inline is
 * enough; the media blocks are left as the no-script answer.
 *
 * The type sizes are the part that is not a straight restatement: each is
 * `deviceSnappedFontPx`, held to `MIN_DEVICE_TYPE_PX`, so `body` at 80% on a
 * scale-1 window is 12.5 CSS px - 10 whole device pixels - and not 10.4.
 */
export function zoomedOutScaleVars(dpr: number, deviceWidth: number, deviceHeight: number): Record<string, string> {
  const scale = pickScale(deviceWidth, deviceHeight);
  const vars: Record<string, string> = { "--hm-scale": String(scale), "--hm-dpr": String(dpr) };
  const floor = minDeviceTypePx(deviceHeight);
  for (const [name, px] of Object.entries(FONT_SIZES)) {
    vars[`--hm-fs-${name}`] = `${String(deviceSnappedFontPx(px, scale, dpr, floor))}px`;
  }
  // The floor itself, in CSS px, for the sizes screens hard-code.
  vars["--hm-type-floor"] = `${String(Math.round((floor / (dpr > 0 ? dpr : 1)) * 1000) / 1000)}px`;
  vars["--hm-cap-floor"] = String(deviceCapFloor(deviceHeight));
  return vars;
}

/**
 * Whether the browser is zoomed OUT, and what the window measures in device
 * pixels if so.
 *
 * Browser zoom is honoured in both directions - the root is no longer
 * transformed back to 100% (the argument is in `@game/render`'s
 * `text/devicePixels.ts`) - so the only thing the theme does about a zoom-out
 * is restate its scale step and type sizes from the device viewport (see
 * `zoomedOutScaleVars`). `null` at 100% and above, which is the case that
 * must leave the markup untouched.
 */
function useZoomOut(): { readonly dpr: number; readonly width: number; readonly height: number } | null {
  const read = (): { dpr: number; width: number; height: number } | null => {
    if (typeof window === "undefined") return null;
    // Chromium at 80% reports 0.8000000119; three decimals is finer than any
    // zoom step a browser offers and keeps 1 / dpr arithmetic clean.
    const raw = window.devicePixelRatio;
    const dpr = raw > 0 ? Math.round(raw * 1000) / 1000 : 1;
    if (dpr >= 1) return null;
    return { dpr, width: window.innerWidth * dpr, height: window.innerHeight * dpr };
  };
  const [state, setState] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const apply = (): void => {
      const next = read();
      setState((prev) => (
        prev?.dpr === next?.dpr && prev?.width === next?.width && prev?.height === next?.height ? prev : next
      ));
    };
    apply();
    // Zoom changes arrive as a resize (the CSS viewport changes size) and as
    // the `(resolution)` query flipping; the ratio is re-read on both.
    window.addEventListener("resize", apply);
    const mq = window.matchMedia("(resolution: 1dppx)");
    mq.addEventListener("change", apply);
    return () => {
      window.removeEventListener("resize", apply);
      mq.removeEventListener("change", apply);
    };
  }, []);
  return state;
}

/**
 * Mounts the stylesheet and opens the scaling scope. Everything else in this
 * package assumes it is somewhere above them, because every metric is a
 * `calc(... * var(--hm-scale))` and that variable is declared on `.hm-root`.
 */
export function GameTheme(props: {
  readonly children: ReactNode;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  /** Pin the scale instead of letting the media steps choose it. Screenshot
   *  harness only: a fixed scale makes two runs byte-comparable. */
  readonly scale?: number | undefined;
}): JSX.Element {
  useStyleSheet(THEME_STYLE_ID, THEME_CSS, StyleLayer.theme);
  useEffect(() => {
    aliasRefs += 1;
    // The aliasing filter is referenced by `url(#hm-alias)` from the sheet, so
    // it has to be an element in this document rather than part of the sheet.
    // It goes in the body and NOT in this provider's own subtree: `GameTheme`
    // nests (the adventure shell mounts its own inside the app's), and two
    // elements sharing an id is a filter that resolves to whichever the
    // renderer happened to see first.
    let defs: Element | null = document.getElementById(ALIAS_DEFS_ID);
    if (defs === null) {
      const host = document.createElement("div");
      host.innerHTML = ALIAS_FILTER;
      defs = host.firstElementChild;
      if (defs !== null) document.body.appendChild(defs);
    }
    return () => {
      aliasRefs -= 1;
      if (aliasRefs === 0) defs?.remove();
    };
  }, []);

  const zoomedOut = useZoomOut();
  const phone = usePhoneLayout();
  const style = useMemo<CSSProperties>(() => {
    const s: Record<string, string | number> = { ...(props.style as object ?? {}) };
    // Under a zoom-out the media steps measure the wrong viewport; the step
    // and the type sizes are restated from the device one. A pinned scale
    // still wins.
    if (zoomedOut !== null) Object.assign(s, zoomedOutScaleVars(zoomedOut.dpr, zoomedOut.width, zoomedOut.height));
    if (props.scale !== undefined) s["--hm-scale"] = String(props.scale);
    return s as CSSProperties;
  }, [props.style, props.scale, zoomedOut]);

  return (
    <div
      className={cx(
        "hm-root",
        phone.phone && "hm-root--phone",
        phone.portrait && "hm-root--portrait",
        props.className,
      )}
      style={style}
      data-layout={phone.landscape ? "phone" : phone.portrait ? "portrait" : "desktop"}
    >
      {props.children}
    </div>
  );
}

/** Join class names, dropping the falsy ones. Kept local: one helper, no dep. */
export function cx(...parts: readonly (string | false | null | undefined)[]): string {
  let out = "";
  for (const p of parts) {
    if (p === false || p === null || p === undefined || p === "") continue;
    out = out === "" ? p : `${out} ${p}`;
  }
  return out;
}

// --------------------------------------------------------------------- panel

export type PanelMaterial = "wood" | "stone" | "parchment";

export function Panel(props: {
  readonly children?: ReactNode | undefined;
  readonly material?: PanelMaterial | undefined;
  /** Drop the drop-shadow, for a panel seated inside another. */
  readonly flush?: boolean | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  readonly title?: string | undefined;
}): JSX.Element {
  const material = props.material ?? "wood";
  return (
    <div
      className={cx(
        "hm-panel",
        material === "stone" && "hm-panel--stone",
        material === "parchment" && "hm-panel--parchment",
        props.flush === true && "hm-panel--flush",
        props.className,
      )}
      style={props.style}
    >
      {props.title !== undefined && <div className="hm-title">{props.title}</div>}
      {props.children}
    </div>
  );
}

// --------------------------------------------------------------------- frame

/** A gilt-edged inner region. `well` sinks it instead of raising it.
 *
 * `as="span"` exists because a `Frame` is often the portrait socket inside a
 * row that is itself a `<button>`, and a `<div>` is not legal button content. */
export function Frame(props: {
  readonly children?: ReactNode | undefined;
  readonly well?: boolean | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  readonly title?: string | undefined;
  readonly as?: "div" | "span" | undefined;
}): JSX.Element {
  const Tag = props.as ?? "div";
  return (
    <Tag
      className={cx("hm-frame", props.well === true && "hm-frame--well", props.className)}
      style={props.as === "span" ? { display: "block", ...props.style } : props.style}
      title={props.title}
    >
      {props.children}
    </Tag>
  );
}

// -------------------------------------------------------------------- button

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: "normal" | "primary" | "icon" | undefined;
}

export function Button({ variant = "normal", className, type, ...rest }: ButtonProps): JSX.Element {
  return (
    <button
      type={type ?? "button"}
      className={cx(
        "hm-btn",
        variant === "primary" && "hm-btn--primary",
        variant === "icon" && "hm-btn--icon",
        className,
      )}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------- slot

/** A recessed socket for a portrait, a creature or an artifact. */
export function Slot(props: {
  readonly children?: ReactNode | undefined;
  readonly size?: number | undefined;
  readonly width?: number | undefined;
  readonly height?: number | undefined;
  readonly selected?: boolean | undefined;
  readonly empty?: boolean | undefined;
  readonly onClick?: (() => void) | undefined;
  readonly onPointerEnter?: (() => void) | undefined;
  readonly onPointerLeave?: (() => void) | undefined;
  readonly title?: string | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
}): JSX.Element {
  const size = props.size;
  const style: CSSProperties = {
    width: props.width ?? size,
    height: props.height ?? size,
    ...props.style,
  };
  const interactive = props.onClick !== undefined;
  const cls = cx(
    "hm-slot",
    interactive && "hm-slot--button",
    props.selected === true && "hm-slot--selected",
    props.empty === true && "hm-slot--empty",
    props.className,
  );
  if (!interactive) {
    return (
      <div
        className={cls}
        style={style}
        title={props.title}
        onPointerEnter={props.onPointerEnter}
        onPointerLeave={props.onPointerLeave}
      >
        {props.children}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      style={style}
      title={props.title}
      aria-pressed={props.selected === true}
      onClick={props.onClick}
      onPointerEnter={props.onPointerEnter}
      onPointerLeave={props.onPointerLeave}
    >
      {props.children}
    </button>
  );
}

// ------------------------------------------------------------------- divider

export function Divider(props: {
  readonly vertical?: boolean | undefined;
  readonly className?: string | undefined;
}): JSX.Element {
  return (
    <div
      role="separator"
      aria-orientation={props.vertical === true ? "vertical" : "horizontal"}
      className={cx("hm-divider", props.vertical === true && "hm-divider--vertical", props.className)}
    />
  );
}

// ------------------------------------------------------------------ statline

/** `label ......... value`, the shape every sheet in the classic uses. */
export function StatLine(props: {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly icon?: ReactNode | undefined;
  readonly title?: string | undefined;
  readonly className?: string | undefined;
}): JSX.Element {
  return (
    <div className={cx("hm-statline", props.className)} title={props.title}>
      {props.icon}
      <span className="hm-statline__label hm-label">{props.label}</span>
      <span className="hm-statline__leader" aria-hidden="true" />
      <span className="hm-statline__value hm-num">{props.value}</span>
    </div>
  );
}

// ----------------------------------------------------------------- gauge bar

export function Bar(props: {
  readonly value: number;
  readonly max: number;
  readonly kind: "move" | "mana";
  readonly title?: string | undefined;
  readonly className?: string | undefined;
}): JSX.Element {
  const max = props.max > 0 ? props.max : 1;
  const pct = Math.max(0, Math.min(100, Math.round((props.value / max) * 100)));
  return (
    <div
      className={cx("hm-bar", props.kind === "move" ? "hm-bar--move" : "hm-bar--mana", props.className)}
      role="meter"
      aria-valuenow={props.value}
      aria-valuemin={0}
      aria-valuemax={max}
      title={props.title}
    >
      <div className="hm-bar__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------------- icon

/**
 * One atlas frame, fitted into a box at a whole-number ratio (`fitSpriteBox`).
 *
 * `size` is the box WIDTH in CSS pixels, and its height too unless `boxHeight`
 * says otherwise. Pass `boxHeight` for any socket that is not square: fitting
 * by the frame's longest side into a square is what drew the town screen's
 * Angel filling its socket and the Spearwarden filling half of it, because the two
 * frames have wildly different aspect ratios and only one of them is as tall as
 * it is wide.
 *
 * A missing key renders the hatched swatch rather than nothing, because a blank
 * gap in a panel is a bug you do not notice.
 */
export function Icon(props: {
  readonly atlasKey: string;
  readonly size: number;
  /** Box height. Defaults to `size`, i.e. a square box. */
  readonly boxHeight?: number | undefined;
  readonly title?: string | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  readonly atlas?: Atlas | undefined;
}): JSX.Element {
  const ctx = useAtlas();
  const atlas = props.atlas ?? ctx;
  const frame = atlas.frame(props.atlasKey);
  const boxH = props.boxHeight ?? props.size;
  const style = useMemo<CSSProperties>(
    () => ({
      ...(spriteStyleBox(atlas, props.atlasKey, props.size, boxH) as CSSProperties),
      ...props.style,
    }),
    [atlas, props.atlasKey, props.size, boxH, props.style],
  );
  const magnified = frame !== null && fitSpriteBox(frame, props.size, boxH).magnified;
  return (
    <span
      className={cx(
        "hm-icon",
        frame === null ? "hm-icon--missing" : magnified ? "hm-icon--up" : "hm-icon--down",
        props.className,
      )}
      style={style}
      title={props.title}
      role={props.title === undefined ? "presentation" : "img"}
      aria-label={props.title}
    />
  );
}

// --------------------------------------------------------------- scroll area

/** A reading's top and bottom edge, in pixels from the top of the viewport it
 *  is being read in. */
export interface ReadingBand {
  readonly top: number;
  readonly bottom: number;
}

/**
 * How many pixels of a scroll viewport's BOTTOM to hide so that nothing is
 * drawn across the fold.
 *
 * A scroll container cuts at whatever pixel its box ends on, and at 1366x768
 * with the browser at 100% the adventure sheet's fold lands in the middle of a
 * secondary-skill row: measured on that screen, `Leadership` / `Basic` stood
 * 10 rows of its 14-row band and the row read as a torn strip of letters. Half
 * a caption is not a smaller caption, it is a defect - the one thing this lane
 * exists to keep off the screen - so the viewport is shortened to the last
 * whole ROW instead, and the rest is reached by scrolling, which is what the
 * container is for.
 *
 * ## Rows, not readings
 *
 * The first cut of this enumerated `svg[data-cap]` and nothing else, so it
 * folded between a row's caption and the row's own art: measured on the
 * adventure sidebar at 1366x768 at 100%, the towns roster was clipped through
 * the MIDDLE of the town thumbnail, with the panel's gold keyline drawn across
 * the sliced image, and the `Castle` line under the name was not painted at
 * all. Half a picture is the same defect as half a caption and it is the one a
 * player photographs. The bands are whole ROWS now - the direct children of
 * the scrolled content - plus any picture or reading inside them, so a fold
 * lands between two rows or not at all.
 *
 * ## And never on nothing
 *
 * Only a band that STRADDLES the fold moves it, so a container whose content
 * ends cleanly is not shortened at all. A band that starts at or above the top
 * of the viewport and still crosses its bottom - one row taller than the whole
 * box - leaves the fold alone: a torn row is bad, an EMPTY panel is worse, and
 * the caller is told (`folded`) so it can show that there is more below.
 */
export function foldInset(readings: readonly ReadingBand[], viewport: number): number {
  let fold = viewport;
  for (const r of readings) {
    if (r.top < viewport && r.bottom > viewport && r.top < fold) fold = r.top;
  }
  const inset = viewport - fold;
  return fold > 0 && inset > 0 ? inset : 0;
}

export function ScrollArea(props: {
  readonly children?: ReactNode | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  readonly onPointerLeave?: (() => void) | undefined;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  // `clip-path` and not a height: it hides the torn row without touching
  // layout, so the ResizeObserver below cannot chase its own tail. Runs after
  // every render (the content of a sheet changes without its box changing) and
  // costs two property reads on a container that is not scrolling, which is
  // all of them until one is.
  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    const apply = (): void => {
      if (el.scrollHeight <= el.clientHeight + 1) {
        if (el.style.clipPath !== "") el.style.clipPath = "";
        if (el.dataset.folded !== undefined) delete el.dataset.folded;
        return;
      }
      const top = el.getBoundingClientRect().top + el.clientTop;
      const bands: ReadingBand[] = [];
      const add = (node: Element): void => {
        const r = node.getBoundingClientRect();
        if (r.height < 1) return;
        bands.push({ top: r.top - top, bottom: r.bottom - top });
      };
      // Whole rows first - the children of the scrolled content - so a fold
      // lands between two of them and never through the middle of one. Then
      // the pictures and readings inside them, which is what a row that is
      // itself taller than the viewport is cut on.
      //
      // "The scrolled content" is not always the container's own first child:
      // the hero sheet wraps its rows in a full-height column, and taking that
      // wrapper's children gave ONE band spanning the whole content, which
      // straddles from row zero and therefore folds nothing at all. So walk
      // down the single-child chain while each link fills its parent, and take
      // the children of the last one.
      let content = el.firstElementChild;
      while (content !== null && content.children.length === 1) {
        const only = content.children[0]!;
        if (only.getBoundingClientRect().height < content.getBoundingClientRect().height - 1) break;
        content = only;
      }
      for (const row of content?.children ?? []) add(row);
      for (const mark of el.querySelectorAll("svg[data-cap], img, .hm-icon")) add(mark);
      const inset = foldInset(bands, el.clientHeight);
      const want = inset > 0 ? `inset(0px 0px ${String(inset)}px 0px)` : "";
      if (el.style.clipPath !== want) el.style.clipPath = want;
      // The affordance. A panel that simply stops carrying its last row reads
      // as a panel with nothing in it; one with a lip at the cut reads as a
      // panel with more below, which is what it is. The lip is drawn INSIDE
      // the container, above the fold line, so the clip does not eat it.
      el.style.setProperty("--hm-fold", `${String(inset)}px`);
      el.dataset.folded = "";
    };
    apply();
    const ro = new ResizeObserver(() => { apply(); });
    ro.observe(el);
    const inner = el.firstElementChild;
    if (inner !== null) ro.observe(inner);
    el.addEventListener("scroll", apply, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", apply);
    };
  });
  return (
    <div
      ref={ref}
      className={cx("hm-scroll", props.className)}
      style={props.style}
      onPointerLeave={props.onPointerLeave}
    >
      {props.children}
    </div>
  );
}

/**
 * Element size in CSS pixels, from a ResizeObserver. The minimap and the canvas
 * slot both need real device pixels and neither may guess them from a media
 * query, so this is the one place the shell reads layout back out of the DOM.
 */
export function useElementSize<T extends HTMLElement>(): {
  readonly ref: RefObject<T>;
  readonly width: number;
  readonly height: number;
} {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const apply = (w: number, h: number): void => {
      setSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    };
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box !== undefined) apply(Math.round(box.width), Math.round(box.height));
    });
    ro.observe(el);
    apply(Math.round(el.clientWidth), Math.round(el.clientHeight));
    return () => ro.disconnect();
  }, []);
  return { ref, width: size.width, height: size.height };
}
